import React, { useMemo, useState } from 'react';
import { Box, Button, InputLabel, Typography } from '@heycater/design-system';
import { Dialog, Grid, IconButton } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import { Field, Form, Formik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import FormField from 'caterers/components/FormField';
import {
  CUSTOMER_LEAD_RECORD_TYPE,
  PHONE_NUMBER_REGEX,
} from 'caterers/constants';
import { webToLeadData } from 'caterers/services/webToLeadData';
import Bugsnag from 'lib/bugsnag';
import { Mouseflow } from 'lib/mouseflow';
import Yup from 'lib/yup';
import FormikFormField from 'shared/components/FormikFormField';
import { webToLeadRequest } from 'shared/services/webToLeadRequest';

const FORM_ID = 'contact-caterer-form';

function ContactCatererForm({
  catererName,
  openContactCatererForm,
  setOpenContactCatererForm,
  citySlug,
}) {
  const { t } = useTranslation('caterers');
  const handleClose = () => {
    setOpenContactCatererForm(false);
  };

  const cities = useMemo(() => t('common:cities', { returnObjects: true }), [
    t,
  ]);
  const [contactFormSubmitted, setContactFormSubmitted] = useState(false);
  const [emailAddress, setEmailAddress] = useState(false);

  const handleSubmit = async (values) => {
    try {
      Mouseflow.notify.formSubmitAttempt(FORM_ID);
      await webToLeadRequest(
        {
          ...values,
          caterer_name: catererName,
          city: cities[citySlug] || citySlug,
          record_type: CUSTOMER_LEAD_RECORD_TYPE,
          comment: `
      -- COMMENT --
      Contact Caterer Button: ${catererName}
      Comment: ${values.comment}
    `,
        },
        webToLeadData
      );
      setContactFormSubmitted(true);
      setEmailAddress(values.email);
      Mouseflow.notify.formSubmitSuccess(FORM_ID);
    } catch {
      Mouseflow.notify.formSubmitFailure(FORM_ID);
      Bugsnag.notify(
        new Error(
          `[Error trying to submit a webtolead form] with data: ${JSON.stringify(
            values
          )}`
        )
      );
    }
  };

  return (
    <>
      <Dialog
        open={openContactCatererForm}
        onClose={handleClose}
        anchor="bottom"
        disableAutoFocus
      >
        <Box position="absolute" right={0} top={0} m={1}>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        {contactFormSubmitted ? (
          <>
            <Box position="absolute" right={0} top={0} m={1}>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box md={{ width: 590 }} p={3} mt={2} pb={4}>
              <StyledBox>
                <CheckCircleIcon fontSize="small" />
                <Typography variant="subtitle1" sm={{ ml: 1 }} color="inherit">
                  {t('contactCaterer.success')}
                </Typography>
              </StyledBox>
              <Typography variant="body2">
                <Trans
                  t={t}
                  i18nKey="contactCaterer.confirmation"
                  values={{ email: emailAddress }}
                  components={[<strong key="strong" />]}
                />
              </Typography>
            </Box>
            <Box textAlign="center" mb={3}>
              <Button
                size="lg"
                variant="secondary"
                role="button"
                aria-label="submit"
                id="contact-caterer-modal-continue"
                onClick={handleClose}
              >
                {t('contactCaterer.continue')}
              </Button>
            </Box>
          </>
        ) : (
          <Box md={{ width: 590 }} p={3} pb={4} mx="auto">
            <Typography variant="subtitle1" component="h1">
              {`${t('contactCaterer.title')} ${catererName}`}
            </Typography>
            <Typography variant="body2" mb={3}>
              {t('contactCaterer.subtitle')}
            </Typography>
            <Formik
              id={FORM_ID}
              initialValues={{
                name: '',
                email: '',
                comment: '',
                phone: '',
              }}
              validationSchema={Yup.object({
                name: Yup.string().required(t('formErrors.isRequired')),
                email: Yup.string()
                  .required(t('formErrors.emailRequired'))
                  .email(t('formErrors.correctFormatEmail')),
                comment: Yup.string().required(t('formErrors.isRequired')),
                phone: Yup.string()
                  .required(t('formErrors.isRequired'))
                  .matches(PHONE_NUMBER_REGEX, t('formErrors.phone')),
              })}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {(formikBag) => {
                return (
                  <Form>
                    <Box pb={2}>
                      <Grid item xs={12}>
                        <Box>
                          <InputLabel required>
                            {t('contactCaterer.message')}
                          </InputLabel>
                          <StyledField
                            placeholder={`${t(
                              'contactCaterer.question1'
                            )} ${catererName} ${t('contactCaterer.question2')}`}
                            name="comment"
                            component={FormField}
                            multiline
                            rows={4}
                          />
                        </Box>
                      </Grid>
                      <Grid container spacing={2}>
                        <Grid container item>
                          <Grid item md={6} xs={12}>
                            <Field
                              required
                              label={t('contactCaterer.name')}
                              placeholder={t('contactCaterer.name')}
                              name="name"
                              component={FormikFormField}
                            />
                          </Grid>
                        </Grid>
                        <Grid item md={6} xs={12}>
                          <Field
                            required
                            label={t('contactCaterer.email')}
                            placeholder={t('contactCaterer.email')}
                            name="email"
                            component={FormikFormField}
                            helperText={t('contactCaterer.helper')}
                          />
                        </Grid>
                        <Grid item md={6} xs={12}>
                          <Field
                            required
                            label={t('contactCaterer.phone')}
                            placeholder={t('contactCaterer.phone')}
                            name="phone"
                            component={FormikFormField}
                            helperText={t('contactCaterer.phoneHelper')}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    <Box textAlign="center">
                      <Button
                        disabled={!formikBag.dirty}
                        size="lg"
                        variant="secondary"
                        type="submit"
                        role="button"
                        aria-label="submit"
                        id="contact-caterer-modal-submit"
                      >
                        <Box display="flex" mr={1}>
                          <SendIcon fontSize="small" />
                        </Box>
                        {t('contactCaterer.submit')}
                      </Button>
                    </Box>
                  </Form>
                );
              }}
            </Formik>
          </Box>
        )}
      </Dialog>
    </>
  );
}

const StyledBox = styled.div`
  color: ${(props) => props.theme.palette.secondary.light};
  display: flex;
  align-items: center;
  padding-bottom: ${(props) => props.theme.spacing(2)}px;

  ${(props) => props.theme.breakpoints.down('xs')} {
    flex-direction: column;
    align-items: start;
  }
`;

const StyledField = styled(Field)`
  .MuiFilledInput-root {
    border-radius: ${(props) => props.theme.spacing(1)}px;
    padding: ${(props) => props.theme.spacing(1.5)}px;
    background-color: ${(props) => props.theme.palette.text.disabled};
    border: 1px solid transparent;
    line-height: ${(props) => props.theme.typography.body1.lineHeight};

    &:hover {
      background-color: ${(props) => props.theme.palette.text.disabled};
      border: 1px solid ${(props) => props.theme.palette.grey[400]};
    }

    &:active,
    &:focus,
    &:focus-visible {
      border: 1px solid ${(props) => props.theme.palette.grey[600]};
    }
  }

  .MuiFilledInput-underline:before {
    border-bottom: none;
  }

  .MuiFilledInput-underline:after {
    border-bottom: none;
  }

  .MuiInputBase-inputMultiline {
    cursor: auto;
  }
`;

export default ContactCatererForm;
